import React, { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';

const StaffFilters = ({ onFiltersChange, onClearFilters }) => {
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    role: 'all',
    availability: 'all'
  });

  const [isExpanded, setIsExpanded] = useState(false);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  // Clear all filters
  const handleClearFilters = () => {
    const clearedFilters = {
      search: '',
      status: 'all',
      role: 'all',
      availability: 'all'
    };
    setFilters(clearedFilters);
    onClearFilters?.();
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return filters?.search !== '' || 
           filters?.status !== 'all' || 
           filters?.role !== 'all' || 
           filters?.availability !== 'all';
  };

  const staffRoles = [
    { value: 'all', label: 'All Roles' },
    { value: 'check_in_staff', label: 'Check-in Staff' },
    { value: 'security_staff', label: 'Security Staff' },
    { value: 'event_coordinator', label: 'Event Coordinator' },
    { value: 'technical_support', label: 'Technical Support' },
    { value: 'catering_staff', label: 'Catering Staff' },
    { value: 'cleaning_staff', label: 'Cleaning Staff' },
    { value: 'ushering_staff', label: 'Ushering Staff' },
    { value: 'parking_staff', label: 'Parking Staff' },
    { value: 'first_aid_staff', label: 'First Aid Staff' }
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-6">
      {/* Search Bar */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search staff by name, email, role, or skills..."
              value={filters?.search}
              onChange={(e) => handleFilterChange('search', e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {hasActiveFilters() && (
              <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                Active
              </span>
            )}
          </button>
          {hasActiveFilters() && (
            <button
              onClick={handleClearFilters}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              title="Clear all filters"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {/* Extended Filters */}
      {isExpanded && (
        <div className="p-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters?.status}
                onChange={(e) => handleFilterChange('status', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            {/* Role Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <select
                value={filters?.role}
                onChange={(e) => handleFilterChange('role', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {staffRoles?.map((role) => (
                  <option key={role?.value} value={role?.value}>
                    {role?.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Availability Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Availability
              </label>
              <select
                value={filters?.availability}
                onChange={(e) => handleFilterChange('availability', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Staff</option>
                <option value="available">Available for Assignment</option>
                <option value="assigned">Currently Assigned</option>
              </select>
            </div>
          </div>

          {/* Filter Summary */}
          {hasActiveFilters() && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                {filters?.search && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                    Search: "{filters?.search}"
                    <button
                      onClick={() => handleFilterChange('search', '')}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters?.status !== 'all' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                    Status: {filters?.status}
                    <button
                      onClick={() => handleFilterChange('status', 'all')}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters?.role !== 'all' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                    Role: {staffRoles?.find(r => r?.value === filters?.role)?.label || filters?.role}
                    <button
                      onClick={() => handleFilterChange('role', 'all')}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters?.availability !== 'all' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                    Availability: {filters?.availability}
                    <button
                      onClick={() => handleFilterChange('availability', 'all')}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffFilters;